import { Flex, Group, useMantineTheme } from '@mantine/core';
import { useMemo } from 'react';

import { Match } from '~/domains/common/components/Match/Match';
import { getCurrentUserTimezoneAbbreviation } from '~/domains/contest/domains/common/utils/playerCardDisplay';
import { Text } from '~/domains/design-system/Text';
import { IconWeather } from '~/domains/design-system/icons/IconWeather';
import dt from '~/domains/contest/domains/game/domains/common/testing.const';

import type { PlayerInfoUpcoming } from '../../../types/player';
import { useContestContext } from '../../../../common/context/ContestContext/ContestContext';

export function PlayerCardUpcoming({
  playerName,
  afterPlayerNameLabel,
  tag,
  nextPlay,
  primaryPosition,
  opposingTeam,
  meta = [],
  game,
  weatherIcon,
}: Omit<PlayerInfoUpcoming, 'playerId' | 'isUpcoming'>) {
  const theme = useMantineTheme();
  const { contest } = useContestContext();
  const shouldShowTimezone = Boolean(contest?.settings?.customContestSettings?.showTimezone);
  const timezoneAbbreviation = useMemo(() => getCurrentUserTimezoneAbbreviation(), []);

  const nextPlayLabel =
    nextPlay && shouldShowTimezone && timezoneAbbreviation
      ? `${nextPlay} ${timezoneAbbreviation}`
      : nextPlay;

  const pitcherInfo = useMemo(() => {
    // Only set this field if the league is MLB
    if (contest?.league?.alias !== 'mlb') return null;
    if (!game) return null;

    const opponentTeam = game.currentTeamId === game.home?.id ? game.away : game.home;
    const pitcherLastName =
      opponentTeam?.startingPitcher?.last_name ?? opponentTeam?.probablePitcher?.last_name;
    if (pitcherLastName) {
      return `SP: ${pitcherLastName}`;
    }
    return 'SP: Undetermined';
  }, [contest?.league?.alias, game]);

  return (
    <>
      <Flex gap={theme.other.spacing._4} align="center">
        <Text
          data-test-id={dt.dfs.playerCard.playerName}
          variant="headline-medium"
          color={theme.colors.gray2[6]}
          truncate
        >
          {playerName}
        </Text>
        {afterPlayerNameLabel && (
          <Text variant="body-small" color={theme.colors.gray2[3]}>
            • {afterPlayerNameLabel}
          </Text>
        )}
        {primaryPosition && (
          <Text variant="subhead-x-small" color={theme.colors.gray2[3]}>
            • {primaryPosition}
          </Text>
        )}
      </Flex>
      <Flex gap={theme.other.spacing._4} sx={{ flexGrow: 1, marginTop: /* eslint-disable-line local-rules/use-theme-tokens */ '2px' }}>
        {tag?.position || tag?.countryAbbr ? (
          <Text variant="subhead-x-small" lh={1} sx={{ flexShrink: 0 }}>
            {tag?.position || tag?.countryAbbr}
          </Text>
        ) : null}
        {nextPlayLabel && !game ? (
          <Text variant="subhead-x-small" color="gray.6" lh={1}>
            {!!(tag?.position || tag?.countryAbbr) && '• '} {nextPlayLabel}
          </Text>
        ) : null}
        {game ? (
          <>
            <Text color={theme.colors.gray2[4]} variant="body-small" lh={1}>
              {game?.currentTeamId === game?.home?.id ? (
                <>
                  <strong>{game?.home?.alias}</strong> vs {game?.away?.alias}
                </>
              ) : (
                <>
                  <strong>{game?.away?.alias}</strong> @ {game?.home?.alias}
                </>
              )}
            </Text>
            <Text color={theme.colors.gray2[3]} variant="body-small" lh={1}>
              {' '}
              •{' '}
            </Text>
            <Text color={theme.colors.gray2[3]} variant="body-small" lh={1}>
              {nextPlayLabel}
            </Text>
          </>
        ) : null}
        {!game && opposingTeam ? (
          <Text variant="body-small" color={theme.colors.gray2[3]} lh={1}>
            vs {opposingTeam}
          </Text>
        ) : null}
        {weatherIcon && (
          <IconWeather weather={weatherIcon} size={14} color={theme.colors.gray2[3]} />
        )}
      </Flex>
      {pitcherInfo && (
        <Flex gap={theme.other.spacing._4} sx={{ flexGrow: 1, marginTop: theme.other.spacing._6 }}>
          <Text variant="body-small" color={theme.colors.gray2[3]} lh={1}>
            {pitcherInfo}
          </Text>
        </Flex>
      )}
      <Group pt={theme.other.spacing._2} spacing={theme.other.spacing._8}>
        {meta.map(({ value, label, variant = 'default' }, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index}>
            <Match value={variant}>
              <Match.On value={['default']}>
                <Text variant="headline-small">{value}</Text>
              </Match.On>

              <Match.On value={['winner']}>
                <Text variant="headline-small" color="green.9">
                  {value}
                </Text>
              </Match.On>

              <Match.On value={['looser']}>
                <Text variant="headline-small" color="red.9">
                  {value}
                </Text>
              </Match.On>
            </Match>

            <Text
              variant="body-small"
              color={theme.colors.gray2[3]}
              data-test-id={dt.dfs.playerCard.abbrItem}
              component="div"
            >
              {label}
            </Text>
          </div>
        ))}
      </Group>
    </>
  );
}
